import { useEffect, useState } from "react";
import { X, Crown, Trophy, Swords, Star, Zap, Sparkles, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { sounds } from "@/lib/sounds";
import { useGeo } from "@/lib/geo";

/**
 * ProfileModal — DarePass-gated player profile card.
 *
 * Strict lock rules:
 *   - Active VIP  → full stat card (All-Time High, Room Wins, #1 Finishes,
 *                    Current Daily Rank) with a golden shimmering name,
 *                    animated gold-magenta border, and DAREPASS VIP badge.
 *   - Free / Guest → NO stats. A compact "🔒 VIP Profile Locked" card with
 *                    a prominent DarePass upsell banner (₹99 IN / $2.99 global).
 *
 * Retention: the backend keeps every stat forever — the lock is purely a
 * display gate. The instant a user renews, the same numbers reappear.
 */
export default function ProfileModal({ userId, onClose }) {
  const navigate = useNavigate();
  const { isIndia } = useGeo();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/profile/${userId}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleUpsell = () => {
    sounds.play("click");
    onClose();
    navigate("/pricing");
  };

  const isPro = !!data?.is_pro;
  const isLocked = !!data?.locked; // backend gate: viewer free + target free
  const initial = (data?.name || "?").charAt(0).toUpperCase();

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="profile-modal-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="profile-modal"
        data-vip={isPro ? "1" : "0"}
        data-locked={isLocked ? "1" : "0"}
        className={`relative w-full max-w-md ${
          isPro ? "vip-profile-frame" : "border border-[#00F0FF]/50 bg-[#0B0C10]"
        }`}
      >
        <div className={`relative ${isPro ? "p-6 sm:p-8" : "p-5 sm:p-6"}`}>
          <button
            data-testid="profile-modal-close"
            onClick={onClose}
            aria-label="Close profile"
            className="absolute -top-3 -right-3 w-9 h-9 flex items-center justify-center bg-[#FF007F] text-black border-2 border-black hover:bg-white hover:text-[#FF007F] hover:border-[#FF007F] transition-colors shadow-[3px_3px_0_#00F0FF] z-10"
          >
            <X size={18} strokeWidth={3} />
          </button>

          {loading && (
            <div className="text-center py-16 text-[#00F0FF] font-mono-arcade text-xs uppercase tracking-[0.3em] animate-pulse">
              Loading profile...
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-16 text-[#FF007F] font-mono-arcade text-xs uppercase tracking-[0.3em]">
              {error}
            </div>
          )}

          {!loading && !error && data && (isPro ? (
            <VipProfileBody data={data} initial={initial} />
          ) : isLocked ? (
            <LockedProfileBody
              data={data}
              initial={initial}
              onUpgrade={handleUpsell}
              isIndia={isIndia}
            />
          ) : (
            // Free target viewed by a DarePass member — reveal the same
            // career numbers but in the standard (non-gold) treatment. The
            // DAREPASS VIP badge/border stays exclusive to actual VIPs.
            <StandardProfileBody data={data} initial={initial} />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Free target viewed by a DarePass member — full stat reveal but
 * WITHOUT the gold VIP treatment. Keeps the visual hierarchy clear:
 * only actual DarePass members earn the gold border + VIP badge, while
 * DarePass members still get their "see everything" privilege.
 */
function StandardProfileBody({ data, initial }) {
  return (
    <div data-testid="standard-profile-body">
      <div className="flex flex-col items-center pt-2 pb-5 border-b border-[#00F0FF]/20">
        <div
          data-testid="profile-avatar"
          className="w-20 h-20 flex items-center justify-center overflow-hidden border-2 border-[#00F0FF]/60"
        >
          {data.picture ? (
            <img
              src={data.picture}
              alt={data.name}
              referrerPolicy="no-referrer"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-[#00F0FF]/10 flex items-center justify-center font-heading font-black text-2xl text-[#00F0FF]">
              {initial}
            </div>
          )}
        </div>
        <div
          data-testid="profile-name"
          className="mt-3 font-heading font-black text-lg text-white tracking-wide"
        >
          {data.name}
        </div>
        {data.player_since && (
          <div className="mt-1 text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]">
            Player Since · {data.player_since}
          </div>
        )}
        {typeof data.current_daily_rank === "number" && (
          <div
            data-testid="profile-daily-rank"
            className="mt-3 flex items-center gap-2 border border-[#00F0FF]/40 bg-[#00F0FF]/10 px-3 py-1"
          >
            <Trophy size={12} strokeWidth={2.5} className="text-[#00F0FF]" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] font-black">
              Current Rank
            </span>
            <span className="font-mono-arcade font-bold text-sm text-white">
              #{data.current_daily_rank}
            </span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 py-4">
        <StatTile
          icon={<Zap size={16} strokeWidth={2.5} />}
          label="All-Time High"
          value={data.all_time_high}
          testId="stat-all-time-high"
          padZeros={5}
          tone="cyan"
        />
        <StatTile
          icon={<Swords size={16} strokeWidth={2.5} />}
          label="Room Wins"
          value={data.room_wins}
          testId="stat-room-wins"
          tone="cyan"
        />
        <StatTile
          icon={<Trophy size={16} strokeWidth={2.5} />}
          label="#1 Finishes"
          value={data.leaderboard_first_finishes}
          testId="stat-first-finishes"
          tone="cyan"
        />
      </div>
    </div>
  );
}

/**
 * Active DarePass VIP: golden shimmering name + full stats reveal.
 */
function VipProfileBody({ data, initial }) {
  return (
    <>
      <div
        data-testid="vip-badge"
        className="absolute -top-3 left-6 flex items-center gap-1.5 bg-[#EAEB18] text-black font-heading font-black text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 shadow-[3px_3px_0_#FF007F]"
      >
        <Crown size={12} strokeWidth={3} />
        DAREPASS VIP
      </div>

      <div className="flex flex-col items-center pt-6 pb-5 border-b border-[#EAEB18]/25">
        {/* Premium avatar: rotating gold ring + crown pip (same treatment as leaderboard) */}
        <div
          data-testid="profile-avatar"
          className="relative w-24 h-24 shrink-0"
        >
          <div className="absolute inset-0 rounded-full darepass-ring" aria-hidden="true" />
          <div className="absolute inset-[3px] rounded-full overflow-hidden bg-[#0B0C10] flex items-center justify-center">
            {data.picture ? (
              <img
                src={data.picture}
                alt={data.name}
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-[#EAEB18]/10 flex items-center justify-center font-heading font-black text-3xl text-[#EAEB18]">
                {initial}
              </div>
            )}
          </div>
          {/* Corner crown pip — mirrors ProAvatarFrame on leaderboard */}
          <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#0B0C10] flex items-center justify-center border-2 border-[#FFD700] shadow-[0_0_8px_rgba(255,215,0,0.85)]">
            <Crown size={12} strokeWidth={3} className="darepass-crown-icon" />
          </div>
          <Sparkles
            size={16}
            strokeWidth={3}
            className="absolute -top-1 -left-1 text-[#EAEB18] animate-pulse"
          />
        </div>
        <div
          data-testid="profile-name"
          className="mt-4 font-heading font-black text-2xl tracking-wide flex items-center gap-2 vip-name-shimmer"
        >
          {data.name}
          <Crown size={18} strokeWidth={2.5} className="text-[#EAEB18]" />
        </div>
        <div
          data-testid="vip-tagline"
          className="mt-1 text-[10px] uppercase tracking-[0.3em] font-black text-[#EAEB18]"
        >
          DarePass · Premium Member
        </div>
        {data.player_since && (
          <div className="mt-1 text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]">
            Player Since · {data.player_since}
          </div>
        )}
        {typeof data.current_daily_rank === "number" && (
          <div
            data-testid="profile-daily-rank"
            className="mt-3 flex items-center gap-2 border border-[#EAEB18]/40 bg-[#EAEB18]/10 px-3 py-1"
          >
            <Trophy size={12} strokeWidth={2.5} className="text-[#EAEB18]" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#EAEB18] font-black">
              Current Rank
            </span>
            <span className="font-mono-arcade font-bold text-sm text-white">
              #{data.current_daily_rank}
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 py-5">
        <StatTile
          icon={<Zap size={16} strokeWidth={2.5} />}
          label="All-Time High"
          value={data.all_time_high}
          testId="stat-all-time-high"
          padZeros={5}
        />
        <StatTile
          icon={<Swords size={16} strokeWidth={2.5} />}
          label="Room Wins"
          value={data.room_wins}
          testId="stat-room-wins"
        />
        <StatTile
          icon={<Trophy size={16} strokeWidth={2.5} />}
          label="#1 Finishes"
          value={data.leaderboard_first_finishes}
          testId="stat-first-finishes"
        />
      </div>
    </>
  );
}

/**
 * Locked profile card — Free / Guest users. No stats leak. Big upsell.
 */
function LockedProfileBody({ data, initial, onUpgrade, isIndia }) {
  // Show the price in the visitor's OWN currency only. India → ₹99 (no
  // USD line). Rest of world → $2.99 (no INR line). Avoids the confusing
  // "₹99 · $2.99" double-price that felt targeted at neither audience.
  const price = isIndia ? "₹99" : "$2.99";
  const priceCadence = "/month";
  return (
    <div data-testid="locked-profile-body" className="flex flex-col items-center py-3">
      <div className="relative">
        <div
          data-testid="profile-avatar"
          className="w-20 h-20 flex items-center justify-center overflow-hidden border-2 border-[#00F0FF]/40 grayscale opacity-70"
        >
          {data.picture ? (
            <img
              src={data.picture}
              alt={data.name}
              referrerPolicy="no-referrer"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-[#00F0FF]/10 flex items-center justify-center font-heading font-black text-2xl text-[#00F0FF]">
              {initial}
            </div>
          )}
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-black/70 rounded-full p-2 border border-[#EAEB18]/60">
            <Lock size={16} strokeWidth={2.5} className="text-[#EAEB18]" />
          </div>
        </div>
      </div>
      <div
        data-testid="profile-name"
        className="mt-4 font-heading font-black text-lg text-white tracking-wide"
      >
        {data.name}
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]">
        {data.is_guest ? "Guest Player" : "Player Since · " + (data.player_since || "—")}
      </div>

      <div
        data-testid="profile-upsell"
        className="mt-6 w-full border-2 border-[#EAEB18] bg-[#EAEB18]/8 p-4"
      >
        <div className="flex items-center gap-2 mb-2">
          <Lock size={14} strokeWidth={3} className="text-[#EAEB18]" />
          <div className="font-heading font-black text-xs uppercase tracking-[0.25em] text-[#EAEB18]">
            VIP Profile Locked
          </div>
        </div>
        <p className="text-[11px] text-[#E2E8F0] leading-relaxed mb-4">
          Upgrade to <strong>DarePass for {price}</strong>
          <span className="text-[#94A3B8]">{priceCadence}</span> to showcase
          your stats, badges and win records with a golden VIP profile card.
        </p>
        <button
          data-testid="profile-upsell-btn"
          onClick={onUpgrade}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#EAEB18] text-black font-heading font-black text-[11px] uppercase tracking-widest px-4 py-3 hover:bg-white transition-colors"
        >
          <Star size={14} strokeWidth={3} />
          Upgrade to DarePass →
        </button>
      </div>
    </div>
  );
}

function StatTile({ icon, label, value, testId, padZeros = 0, tone = "gold" }) {
  const displayValue =
    padZeros > 0 ? String(value ?? 0).padStart(padZeros, "0") : String(value ?? 0);
  const borderClass =
    tone === "cyan" ? "border-[#00F0FF]/30" : "border-[#EAEB18]/50";
  const valueClass =
    tone === "cyan" ? "text-[#00F0FF]" : "text-[#EAEB18] glow-yellow";
  return (
    <div
      data-testid={testId}
      className={`border ${borderClass} bg-black/40 p-2.5 text-center`}
    >
      <div className={`flex items-center justify-center mb-1 ${valueClass}`}>
        {icon}
      </div>
      <div className={`font-mono-arcade font-bold text-lg ${valueClass}`}>
        {displayValue}
      </div>
      <div className="mt-1 text-[8px] uppercase tracking-[0.2em] text-[#94A3B8] leading-tight">
        {label}
      </div>
    </div>
  );
}
